import { Save } from "lucide-react";
import React from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { ScaledThumbnail } from "./ScaledThumbnail";

interface AlertSettingsThumbnailProps {
  isHovered?: boolean;
  alwaysVisible?: boolean;
}

const THRESHOLDS = [
  {
    label: "Healthy threshold (£K)",
    value: "100",
    hint: "Cash above this amount = green zone",
  },
  {
    label: "Caution threshold (£K)",
    value: "50",
    hint: "Cash below this amount = amber zone",
  },
];

const TOGGLES = [
  {
    label: "Enable Email Notifications",
    hint: "Master switch for all email notifications",
    checked: true,
  },
  {
    label: "Alert Threshold Notifications",
    hint: "Notify users when cash flow falls below thresholds",
    checked: true,
  },
  {
    label: "System Update Notifications",
    hint: "Notify administrators about system updates",
    checked: false,
  },
];

export default function AlertSettingsThumbnail({
  isHovered = false,
  alwaysVisible = false,
}: AlertSettingsThumbnailProps) {
  return (
    <ScaledThumbnail
      isHovered={isHovered}
      alwaysVisible={alwaysVisible}
      contentWidth={340}
    >
      <Card className="w-[340px]">
        <CardHeader>
          <CardTitle>Alert Settings</CardTitle>
          <CardDescription>
            Configure cash flow alert thresholds and notifications
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            {THRESHOLDS.map((threshold) => (
              <div key={threshold.label} className="space-y-2">
                <Label>{threshold.label}</Label>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground text-sm">£</span>
                  <div className="w-32 h-9 rounded-md border border-input bg-background px-3 flex items-center text-sm">
                    {threshold.value}
                  </div>
                  <span className="text-muted-foreground text-sm">K</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {threshold.hint}
                </p>
              </div>
            ))}
          </div>

          <Separator />

          {TOGGLES.map((toggle) => (
            <div
              key={toggle.label}
              className="flex items-center justify-between"
            >
              <div className="space-y-0.5">
                <Label>{toggle.label}</Label>
                <p className="text-sm text-muted-foreground">{toggle.hint}</p>
              </div>
              <Switch checked={toggle.checked} />
            </div>
          ))}

          <div className="flex justify-end">
            <Button>
              <Save className="h-4 w-4 mr-2" />
              Save Alert Settings
            </Button>
          </div>
        </CardContent>
      </Card>
    </ScaledThumbnail>
  );
}

export { AlertSettingsThumbnail };
